import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
  Button, Input, Text, Spinner, Badge,
  Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell,
  makeStyles, tokens,
} from '@fluentui/react-components';
import { PersonAdd24Regular, Dismiss16Regular } from '@fluentui/react-icons';
import { api } from '../services/api';
import { useIsMobile } from '../hooks/useIsMobile';

interface ManageUsersProps {
  open: boolean;
  onClose: () => void;
}

const useStyles = makeStyles({
  inviteRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalM,
    alignItems: 'flex-end',
  },
  mobileInviteRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalM,
  },
  tableContainer: {
    maxHeight: '400px',
    overflowY: 'auto' as any,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  mobileUserCard: {
    padding: '12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statusMsg: {
    marginBottom: tokens.spacingVerticalS,
  },
  successText: {
    color: tokens.colorPaletteGreenForeground1,
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
  },
  warningBanner: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
    borderRadius: tokens.borderRadiusMedium,
    padding: '12px 16px',
    marginBottom: tokens.spacingVerticalM,
  },
});

export const ManageUsersDialog: React.FC<ManageUsersProps> = ({ open, onClose }) => {
  const styles = useStyles();
  const isMobile = useIsMobile();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');
  const [configured, setConfigured] = useState(true);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const [userData, statusData] = await Promise.all([
        api.getUsers(),
        api.getInviteStatus(),
      ]);
      setUsers(userData);
      setConfigured(statusData.configured);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchUsers();
  }, [open, fetchUsers]);

  const handleInvite = async () => {
    if (!inviteEmail.includes('@')) return;
    setInviting(true);
    setMessage('');
    try {
      const result = await api.inviteUser(inviteEmail, inviteName || undefined);
      setMessage(result.message);
      setMessageType('success');
      setInviteEmail('');
      setInviteName('');
      fetchUsers();
    } catch (err: any) {
      setMessage(err.message || 'Failed to send invitation');
      setMessageType('error');
    } finally {
      setInviting(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Remove ${name} from BalanceScript? This will delete all their accounts and transactions.`)) return;
    try {
      await api.deleteUser(id);
      setMessage(`${name} has been removed`);
      setMessageType('success');
      setUsers(prev => prev.filter(u => u.id !== id));
    } catch (err: any) {
      setMessage(err.message || 'Failed to remove user');
      setMessageType('error');
    }
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString();

  return (
    <Dialog open={open} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface style={isMobile
        ? { maxWidth: '100vw', width: '100vw', margin: 0, borderRadius: '12px 12px 0 0', position: 'fixed', bottom: 0, maxHeight: '90vh' }
        : { maxWidth: '700px', width: '90vw' }
      }>
        <DialogTitle>Manage Users</DialogTitle>
        <DialogBody>
          <DialogContent>
            {!configured && (
              <div className={styles.warningBanner}>
                <Text weight="semibold" block>Graph API Not Configured</Text>
                <Text size={200}>
                  Add <code>AZURE_CLIENT_SECRET</code> to your .env file and grant admin consent for
                  <code> User.Invite.All</code> and <code>AppRoleAssignment.ReadWrite.All</code> permissions
                  in Azure to enable invitations.
                </Text>
              </div>
            )}

            {/* Invite form */}
            <div className={isMobile ? styles.mobileInviteRow : styles.inviteRow}>
              <div style={{ flex: 2 }}>
                <Text size={200} weight="semibold" block>Email</Text>
                <Input
                  value={inviteEmail}
                  onChange={(e, d) => setInviteEmail(d.value)}
                  placeholder="user@example.com"
                  type="email"
                  style={{ width: '100%' }}
                  disabled={!configured || inviting}
                />
              </div>
              <div style={{ flex: 1 }}>
                <Text size={200} weight="semibold" block>Name (optional)</Text>
                <Input
                  value={inviteName}
                  onChange={(e, d) => setInviteName(d.value)}
                  placeholder="Display name"
                  style={{ width: '100%' }}
                  disabled={!configured || inviting}
                />
              </div>
              <Button
                appearance="primary"
                icon={<PersonAdd24Regular />}
                onClick={handleInvite}
                disabled={!configured || inviting || !inviteEmail.includes('@')}
                style={isMobile ? { width: '100%' } : {}}
              >
                {inviting ? 'Sending...' : 'Invite'}
              </Button>
            </div>

            {message && (
              <div className={styles.statusMsg}>
                <Text className={messageType === 'success' ? styles.successText : styles.errorText}>
                  {message}
                </Text>
              </div>
            )}

            {/* User list */}
            {loading ? (
              <Spinner label="Loading users..." />
            ) : (
              <div className={styles.tableContainer}>
                {isMobile ? (
                  users.map(user => (
                    <div key={user.id} className={styles.mobileUserCard}>
                      <div>
                        <Text weight="semibold" block>{user.display_name || 'Unknown'}</Text>
                        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{user.email}</Text>
                        <div style={{ marginTop: '4px' }}>
                          <Badge size="small" color={user.role === 'Admin' ? 'brand' : 'informative'} appearance="filled">
                            {user.role}
                          </Badge>
                          <Text size={100} style={{ marginLeft: '8px', color: tokens.colorNeutralForeground3 }}>
                            Since {formatDate(user.created_at)}
                          </Text>
                        </div>
                      </div>
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<Dismiss16Regular />}
                        onClick={() => handleDelete(user.id, user.display_name)}
                        title="Remove user"
                      />
                    </div>
                  ))
                ) : (
                  <Table size="small">
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell>Name</TableHeaderCell>
                        <TableHeaderCell>Email</TableHeaderCell>
                        <TableHeaderCell>Role</TableHeaderCell>
                        <TableHeaderCell>Joined</TableHeaderCell>
                        <TableHeaderCell style={{ width: '60px' }}></TableHeaderCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map(user => (
                        <TableRow key={user.id}>
                          <TableCell>{user.display_name || 'Unknown'}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <Badge size="small" color={user.role === 'Admin' ? 'brand' : 'informative'} appearance="filled">
                              {user.role}
                            </Badge>
                          </TableCell>
                          <TableCell>{formatDate(user.created_at)}</TableCell>
                          <TableCell>
                            <Button
                              appearance="subtle"
                              size="small"
                              icon={<Dismiss16Regular />}
                              onClick={() => handleDelete(user.id, user.display_name)}
                              title="Remove user"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}
          </DialogContent>
        </DialogBody>
        <DialogActions>
          <Button appearance="secondary" onClick={onClose}>Close</Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};
